/**
 * RoleGuard Component - Vendor App
 * 
 * Enforces that only users with 'vendor' role can access this app.
 * This is a security hardwall - no exceptions.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from '@/lib/api';
import { logout as authLogout, isAuthenticated as checkAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';

// ============================================================================
// TYPES
// ============================================================================

type AppRole = 'buyer' | 'vendor' | 'rider' | 'admin';

interface JwtPayload {
  userId: string;
  role: AppRole;
  exp?: number;
}

type RoleMismatchReason = 'NO_TOKEN' | 'INVALID_TOKEN' | 'MISSING_ROLE' | 'ROLE_NOT_ALLOWED' | null;

// ============================================================================
// CONSTANTS
// ============================================================================

const ALLOWED_ROLES: AppRole[] = ['vendor'];
const APP_NAME = 'Vendor';
const REDIRECT_DELAY_MS = 5000;

// ============================================================================
// UTILITIES
// ============================================================================

function decodeToken(token: string | null): JwtPayload | null {
  if (!token) return null;

  try {
    const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
    const parts = cleanToken.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded);

    if (!parsed.userId || !parsed.role) return null;

    // Normalize legacy 'ops' role to 'admin'
    if (parsed.role === 'ops') parsed.role = 'admin';

    return parsed as JwtPayload;
  } catch {
    return null;
  }
}

function getRoleMismatchReason(payload: JwtPayload | null): RoleMismatchReason {
  if (!payload) return 'NO_TOKEN';
  if (!payload.role) return 'MISSING_ROLE';
  if (!ALLOWED_ROLES.includes(payload.role)) return 'ROLE_NOT_ALLOWED';
  return null;
}

// ============================================================================
// COMPONENT
// ============================================================================

interface RoleGuardProps {
  children: React.ReactNode;
}

export function RoleGuard({ children }: RoleGuardProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const token = getAccessToken();
    const isAuthenticated = checkAuth();
    
    // If not authenticated, let the normal auth flow handle it
    if (!isAuthenticated || !token) {
      setStatus('allowed'); // Allow rendering, AuthGuard will redirect to login
      return;
    }

    // Decode and validate token
    const payload = decodeToken(token);
    const reason = getRoleMismatchReason(payload);

    if (reason === null) {
      // Role is allowed
      setStatus('allowed');
      logger.info('RoleGuard: Access granted', { role: payload?.role });
    } else {
      // Role mismatch - HARD DENY
      logger.error('RoleGuard: Access denied', new Error(`Reason: ${reason}, Role: ${payload?.role}`));

      setStatus('denied');
      
      switch (reason) {
        case 'NO_TOKEN':
          setErrorMessage('Please log in to continue.');
          break;
        case 'MISSING_ROLE':
          setErrorMessage('Your account does not have a valid role. Please contact support.');
          break;
        case 'ROLE_NOT_ALLOWED':
          setErrorMessage(
            `This account is not allowed to use the ${APP_NAME} app. ` +
            `Your account has role: ${payload?.role}. ` +
            `Please log in with a ${APP_NAME} account.`
          );
          break;
        default:
          setErrorMessage('Access denied.');
      }

      // Clear auth and redirect after delay
      setTimeout(() => {
        authLogout();
        navigate('/login', { replace: true });
      }, REDIRECT_DELAY_MS);
    }
  }, [navigate]);

  // Still checking
  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (status === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-destructive/5 p-4">
        <div className="max-w-md w-full bg-card rounded-2xl shadow-xl p-8 text-center border">
          {/* Error Icon */}
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Access Denied
          </h1>

          {/* Message */}
          <p className="text-muted-foreground mb-6">
            {errorMessage}
          </p>

          {/* Redirect Notice */}
          <p className="text-sm text-muted-foreground mb-6">
            Redirecting to login in {REDIRECT_DELAY_MS / 1000} seconds...
          </p>

          {/* Manual Redirect Button */}
          <button
            onClick={() => {
              authLogout();
              navigate('/login', { replace: true });
            }}
            className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Access allowed
  return <>{children}</>;
}

export default RoleGuard;

